"use client";

import { useEffect } from "react";
import { CONSENT_CHANGE_EVENT, readChoice, type ConsentChoice } from "@/lib/consent";
import { SITE } from "@/site";

// Each genre site has its own GA4 property; null until it is created.
const GA_ID = SITE.gaId ?? "";
const GA_COOKIE = /^_ga(_|$)/;

type AnalyticsWindow = Window & { dataLayer?: unknown[] } & Record<string, unknown>;

function loadGtag() {
  const w = window as unknown as AnalyticsWindow;
  w[`ga-disable-${GA_ID}`] = false;
  if (document.querySelector(`script[src*="gtag/js"]`)) return;

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head.appendChild(script);

  w.dataLayer = w.dataLayer || [];
  // gtag.js only reads Arguments objects from the dataLayer; a plain array is ignored
  // (the tag then loads but never sends a hit, which is what happened before 2026-10-12)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  }
  gtag("js", new Date());
  gtag("config", GA_ID);
}

// Stops a running tag and expires its cookies on the host and every parent domain
function disableAnalytics() {
  const w = window as unknown as AnalyticsWindow;
  w[`ga-disable-${GA_ID}`] = true;
  const parts = window.location.hostname.split(".");
  const domains = ["", ...parts.slice(0, -1).map((_, i) => `;domain=.${parts.slice(i).join(".")}`)];
  for (const pair of document.cookie.split(";")) {
    const name = pair.split("=")[0].trim();
    if (!GA_COOKIE.test(name)) continue;
    for (const domain of domains) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domain}`;
    }
  }
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

// GA4 loads only after the visitor allowed Analytics in the consent window, and then on the
// first interaction (keeps it off the critical path). Without consent no request leaves the page.
export function GoogleAnalytics() {
  useEffect(() => {
    if (!GA_ID) return;
    const events: (keyof WindowEventMap)[] = [
      "scroll",
      "click",
      "touchstart",
      "keydown",
    ];
    let granted = readChoice()?.a === 1;
    let interacted = false;

    if (!granted) disableAnalytics();

    function onInteraction() {
      interacted = true;
      events.forEach((e) => window.removeEventListener(e, onInteraction));
      if (granted) loadGtag();
    }

    function onConsentChange(event: Event) {
      granted = (event as CustomEvent<ConsentChoice>).detail?.a === 1;
      if (!granted) disableAnalytics();
      // Saving a choice is itself an interaction
      else if (interacted) loadGtag();
    }

    events.forEach((e) =>
      window.addEventListener(e, onInteraction, { once: true, passive: true })
    );
    window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChange);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onInteraction));
      window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChange);
    };
  }, []);

  return null;
}
